import * as LabelPrimitive from '@radix-ui/react-label';
import { useId, type ReactNode } from 'react';
import { FieldContext, type FieldContextValue } from './field-context';
import { cn } from './utils';

export { useField, useFieldOptional } from './field-context';
export type { FieldContextValue, FieldControlProps } from './field-context';

export interface FieldProps {
  readonly label: ReactNode;
  /** Texto de apoio permanente. Vira `aria-describedby` do controle. */
  readonly description?: ReactNode;
  /** Mensagem de erro. Presente → o controle recebe `aria-invalid`. */
  readonly error?: string | undefined;
  readonly required?: boolean;
  /** Esconde o rótulo visualmente, mantendo-o para tecnologia assistiva. */
  readonly hideLabel?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
  /** Conteúdo à direita do rótulo (contador de caracteres, ação secundária). */
  readonly labelAddon?: ReactNode;
}

export function Field({
  label,
  description,
  error,
  required = false,
  hideLabel = false,
  className,
  children,
  labelAddon,
}: FieldProps) {
  const base = useId();
  const value: FieldContextValue = {
    controlId: `${base}-control`,
    descriptionId: `${base}-description`,
    errorId: `${base}-error`,
    hasError: Boolean(error),
    required,
  };

  return (
    <FieldContext.Provider value={value}>
      <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
        <div className="flex items-baseline justify-between gap-3">
          <LabelPrimitive.Root
            htmlFor={value.controlId}
            className={cn('text-sm font-medium text-content', hideLabel && 'qc-sr-only')}
          >
            {label}
            {required ? (
              <>
                {/* O asterisco é decorativo; a obrigatoriedade real chega ao
                    leitor de tela por `aria-required` e pelo texto abaixo. */}
                <span aria-hidden="true" className="ml-0.5 text-danger-content">
                  *
                </span>
                <span className="qc-sr-only"> (obrigatório)</span>
              </>
            ) : null}
          </LabelPrimitive.Root>
          {labelAddon}
        </div>

        {children}

        {description ? (
          <p id={value.descriptionId} className="text-xs text-content-subtle">
            {description}
          </p>
        ) : null}

        {/*
          `role="alert"`: quando a validação roda no submit, a mensagem precisa
          ser anunciada na hora. Ícone + texto além da cor — WCAG 1.4.1 proíbe
          usar cor como único veículo de informação.
        */}
        {error ? (
          <p
            id={value.errorId}
            role="alert"
            className="flex items-start gap-1.5 text-xs font-medium text-danger-content"
          >
            <ErrorGlyph />
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

function ErrorGlyph({ size = 14 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
      className="mt-px shrink-0"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5" />
      <path d="M12 16.5h.01" />
    </svg>
  );
}

/** Bloco de erro do formulário inteiro (não ligado a um campo específico). */
export function FormError({
  children,
  className,
  title,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly title?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border border-danger bg-danger-subtle px-3 py-2.5 text-sm text-danger-content',
        className,
      )}
    >
      <ErrorGlyph size={16} />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Conjunto de campos relacionados, com legenda visível. */
export function Fieldset({
  legend,
  description,
  children,
  className,
}: {
  readonly legend: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const descriptionId = useId();
  return (
    <fieldset
      className={cn('m-0 min-w-0 border-0 p-0', className)}
      aria-describedby={description ? descriptionId : undefined}
    >
      <legend className="mb-1 text-sm font-semibold text-content">{legend}</legend>
      {description ? (
        <p id={descriptionId} className="mb-3 text-xs text-content-subtle">
          {description}
        </p>
      ) : null}
      {children}
    </fieldset>
  );
}
